package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 수업 시간 정의(§S1).
 *
 * 현행 `"월,수,금 19:00-21:00"` 문자열로는 "오늘 수업"을 쿼리할 방법이 없다.
 * [daysOfWeek] array-contains + [startMinuteOfDay] asc 인덱스로 조회한다(§S8).
 */
@Serializable
data class ClassSchedule(
    /** ISO-8601 요일: 1=월 … 7=일. */
    val daysOfWeek: List<Int> = emptyList(),
    /** 자정부터의 분. 학원 `timeZone` 기준 벽시계다(R12). */
    val startMinuteOfDay: Int = 0,
    val endMinuteOfDay: Int = 0,
) {
    fun occursOn(dayOfWeek: Int): Boolean = dayOfWeek in daysOfWeek

    val startLabel: String get() = formatMinuteOfDay(startMinuteOfDay)

    val endLabel: String get() = formatMinuteOfDay(endMinuteOfDay)

    val daysLabel: String
        get() = daysOfWeek.sorted().joinToString(",") { DAY_NAMES.getOrElse(it - 1) { "?" } }

    /** 표시 전용. 저장·쿼리는 항상 구조화 필드를 쓴다. */
    val displayLabel: String get() = "$daysLabel $startLabel-$endLabel"
}

/**
 * 오늘 수업 목록 한 줄(v4 대시보드 수업 테이블).
 *
 * `ClassData`(카탈로그 행)와 달리 오늘의 진행 상태·출결 카운트가 붙은 read model이다.
 */
@Serializable
data class TodayClass(
    val classId: String,
    val academyId: String,
    val className: String,
    val subject: String,
    val teacher: String,
    val schedule: ClassSchedule,
    val enrolledCount: Int = 0,
    val checkedInCount: Int = 0,
    val state: ClassProgressState = ClassProgressState.SCHEDULED,
)

/**
 * v4 수업 상태칩.
 *
 * 각 상태의 정확한 판정 기준은 시안에만 있고 문서에 없다(계획 §13 Q18).
 * 파생 규칙은 서버가 확정할 때까지 열어 두고 여기서는 표시 축만 고정한다.
 */
@Serializable
enum class ClassProgressState(val displayName: String) {
    SCHEDULED("예정"),
    IN_PROGRESS("진행중"),

    /** 종료됐는데 미처리 학생이 남은 상태로 추정. Q18에서 확정한다. */
    NEEDS_REVIEW("확인 필요"),
    DONE("종료"),
}

/** v4 대시보드 상단 전체. 통계 4타일 + 오늘 수업 테이블을 한 번에 받는다. */
@Serializable
data class TodayDashboard(
    val academyId: String,
    val serviceDate: String,
    val summary: AttendanceSummary,
    val classes: List<TodayClass> = emptyList(),
)

private val DAY_NAMES = listOf("월", "화", "수", "목", "금", "토", "일")

private fun formatMinuteOfDay(minuteOfDay: Int): String {
    val hour = (minuteOfDay / 60).coerceIn(0, 23)
    val minute = (minuteOfDay % 60).coerceIn(0, 59)
    return "${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}"
}
